// Shopping List Item Form - Bulk Entry
import React, { useState } from 'react';
import ResponsiveModal from './ResponsiveModal';
import { ShoppingCart, Plus, X } from 'lucide-react';

interface ShoppingListFormProps {
  onAdd: (item: { title: string; notes?: string; metadata?: any }) => void;
}

interface ShoppingItem {
  id: string;
  name: string;
  quantity: string;
}

export default function ShoppingListForm({ onAdd }: ShoppingListFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [listName, setListName] = useState('');
  const [items, setItems] = useState<ShoppingItem[]>([
    { id: '1', name: '', quantity: '' }
  ]);

  const handleAddRow = () => {
    if (items.length >= 30) return; // Max 30 items
    setItems([...items, { id: Date.now().toString(), name: '', quantity: '' }]);
  };

  const handleRemoveRow = (id: string) => {
    if (items.length === 1) return; // Keep at least one row
    setItems(items.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, field: 'name' | 'quantity', value: string) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Filter out empty items and add them all
    const validItems = items.filter(item => item.name.trim());
    
    if (validItems.length === 0) return;
    if (!listName.trim()) {
      alert('Please give your shopping list a name!');
      return;
    }

    validItems.forEach(item => {
      onAdd({
        title: item.name.trim(),
        notes: item.quantity ? `Qty: ${item.quantity}` : undefined,
        metadata: {
          type: 'shopping',
          listName: listName.trim(),
          itemName: item.name.trim(),
          quantity: item.quantity || null
        }
      });
    });

    // Reset form
    setListName('');
    setItems([{ id: Date.now().toString(), name: '', quantity: '' }]);
    setIsOpen(false);
  };

  return (
    <>
      {/* Add Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full md:w-auto px-4 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation"
      >
        <ShoppingCart className="w-5 h-5" />
        Add Shopping List Items
      </button>

      {/* Form Modal */}
      <ResponsiveModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="🛒 Shopping List"
        maxWidth="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* List Name */}
          <div>
            <label className="block text-sm font-semibold text-slate-300 mb-2">
              Shopping List Name *
            </label>
            <input
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., Sadie's Groceries, Weekly Shopping, Party Supplies"
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
              required
              autoFocus
            />
            <p className="text-xs text-slate-500 mt-1">Give this shopping list a unique name to identify it</p>
          </div>

          {/* Instructions */}
          <p className="text-sm text-slate-400">
            Add up to 30 items at once. Fill in item names and optional quantities, then submit all at once.
          </p>

          {/* Item Rows */}
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {items.map((item, index) => (
              <div key={item.id} className="flex gap-2 items-start">
                {/* Row Number */}
                <div className="flex-shrink-0 w-8 pt-3 text-sm text-slate-500 font-semibold">
                  {index + 1}.
                </div>

                {/* Item Name */}
                <div className="flex-1">
                  <input
                    type="text"
                    value={item.name}
                    onChange={(e) => handleUpdateItem(item.id, 'name', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                    placeholder="Item name"
                    autoFocus={index === 0}
                  />
                </div>

                {/* Quantity */}
                <div className="w-32">
                  <input
                    type="text"
                    value={item.quantity}
                    onChange={(e) => handleUpdateItem(item.id, 'quantity', e.target.value)}
                    className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 text-base"
                    placeholder="Qty"
                  />
                </div>

                {/* Remove Button */}
                <button
                  type="button"
                  onClick={() => handleRemoveRow(item.id)}
                  disabled={items.length === 1}
                  className="flex-shrink-0 p-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Remove item"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          {/* Add Another Item Button */}
          {items.length < 30 && (
            <button
              type="button"
              onClick={handleAddRow}
              className="w-full py-2 px-4 bg-slate-700 hover:bg-slate-600 text-slate-300 font-medium rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Another Item ({items.length}/30)
            </button>
          )}

          {/* Item Count */}
          <div className="text-sm text-slate-400 text-center">
            {items.filter(i => i.name.trim()).length} item(s) ready to add
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={items.filter(i => i.name.trim()).length === 0}
            className="w-full py-4 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors touch-manipulation text-lg md:text-base mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ShoppingCart className="w-5 h-5" />
            Add {items.filter(i => i.name.trim()).length} Item(s) to Shopping List
          </button>
        </form>
      </ResponsiveModal>
    </>
  );
}
